import React, { useMemo } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Grid,
  Divider,
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionActions,
  AccordionDetails,
  Typography,
  Snackbar,
  Chip,
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import StatusIcon from '../../../components/common/StatusIcon';
import { getErrorMessage } from '../../../util/errors';
import { FragmentType, graphql, useFragment } from '../../../__generated__';
import { SendMode } from '../../../__generated__/graphql';
import {
  canCancelRequest,
  displayMoney,
  getExchangeInfo,
  isAirTel_Congo,
  isMtn,
  isOrange,
  isRequestEditable,
} from 'gmd-utilities-package';
import { supportsMomo } from 'gmd-utilities-package';
import useCommonNavigations from '../../../hooks/Navigation';
import { useLocation } from 'react-router';
import {
  cancelTransactionMutation,
  confirmAndSubmitTransactionMutation,
  confirmTransactionMutation,
  duplicatedTransaction,
  submitTransactionMutation,
} from '../common';
import { TransactionFormFragment } from './RequestForm';
import SearchClickItem from '../../../components/common/SearchClickItem';
import { serviceTypes } from '../../services/ServiceTypes';

const RequestItemTransactionFragment = graphql(/* GraphQL */ `
  fragment RequestItemTransaction on Transaction {
    ptn
    state
    errors {
      code
      message
    }
    ...TransactionFormTransaction
  }
`);

export const getTransactionQuery = graphql(/* GraphQL */ `
  query Transaction($transactionId: ID!) {
    transaction(id: $transactionId) {
      dateCreated
      ...RequestItemTransaction
    }
  }
`);

export type RequestInfo = {
  // processing?: boolean;
  loading?: boolean;
};

export type RequestItemProps = {
  transaction: FragmentType<typeof RequestItemTransactionFragment>;
  setSearchText: (text: string) => void;
  highlightRecipient?: boolean;
  tint?: boolean;
} & RequestInfo;

interface PhoneInfo {
  phone?: string;
}

interface RequestChipInfo {
  chipLabel: string;
  backgroundColor?: string;
}

const RequestChip = ({
  chipLabel,
  backgroundColor = 'primary',
}: RequestChipInfo) => {
  return (
    <Chip
      label={chipLabel}
      variant="outlined"
      color="primary"
      style={{ backgroundColor: backgroundColor }}
      size="small"
    />
  );
};

const PhoneTypeChip = (props: PhoneInfo) => {
  const phoneType = useMemo(() => {
    let isOrangePhone: boolean = false;
    let isAirTelPhone = false;
    if (props.phone) {
      const isMtnPhone = isMtn(props.phone);
      if (!isMtnPhone) {
        isOrangePhone = isOrange(props.phone);
        if (!isOrangePhone) {
          isAirTelPhone = isAirTel_Congo(props.phone);
          if (isAirTelPhone) {
            return 'AirTel';
          }
        } else {
          return 'Orange';
        }
      } else {
        return 'MTN';
      }
    }
    return 'Unkown';
  }, [props.phone]);
  let chipColor = 'primary';
  if (phoneType === 'Orange') chipColor = 'secondary';

  return <RequestChip chipLabel={phoneType} backgroundColor={chipColor} />;
};

const RequestItem = (props: RequestItemProps) => {
  const {
    // processing,
    // loading: parentLoading,
    highlightRecipient,
    setSearchText,
  } = props;
  const ptnTrx = useFragment(RequestItemTransactionFragment, props.transaction);
  const transaction = useFragment(TransactionFormFragment, ptnTrx);
  const { loading: refreshing, error } = useQuery(getTransactionQuery, {
    variables: { transactionId: transaction?.id || '' },
    pollInterval: 2000,
    skip: transaction?.id === undefined || transaction?.status !== 'SUBMITTED',
  });
  const displayedMoney =
    transaction.sendMode === SendMode.Send
      ? transaction.sent
      : transaction.received;
  const canProcessMomo = supportsMomo(transaction.recipient?.phone || '');
  const { from: sendExchange, to: receiveExchange } = getExchangeInfo(
    transaction.sent.currency,
    transaction.received.currency,
    transaction.exchangeRate
  );
  const isBillPay = transaction.bill?.payItemId ? true : false;
  const [confirmTransaction, confirmResult] = useMutation(
    confirmTransactionMutation
  );
  const [submitTransaction, submitResult] = useMutation(
    submitTransactionMutation
  );
  const [confirmAndSubmitTransaction, confirmAndSubmitResult] = useMutation(
    confirmAndSubmitTransactionMutation
  );
  const [cancelTransaction, cancelResult] = useMutation(
    cancelTransactionMutation
  );
  const { goToAgentRequest, goToRequest, goToReceipt, goToCustomerBills } =
    useCommonNavigations();
  const location = useLocation();
  const [showSnack, setShowSnack] = React.useState(false);

  const onDuplicate = () => {
    if (!transaction.bill?.payItemId) {
      duplicatedTransaction(ptnTrx);
      goToAgentRequest(location.pathname);
    } else {
      const serviceId = transaction.bill.serviceId || '';
      const serviceName = serviceTypes['SEARCHABLE_BILL'].dataForm; //TODO: store dataform on the transaction and pull it from there
      const customerNumber = transaction.bill.serviceNumber || '';
      duplicatedTransaction(ptnTrx);
      goToCustomerBills(
        serviceName,
        serviceId,
        customerNumber,
        location.pathname
      );
    }
  };
  const onViewReceipt = () => {
    // TODO
    goToReceipt(transaction.id, location.pathname);
  };
  const onEdit = () => {
    goToRequest(transaction.id, location.pathname);
  };
  const onCopyPtn = () => {
    if (ptnTrx.ptn) {
      navigator.clipboard.writeText(`${ptnTrx.ptn}`);
      setShowSnack(true);
    }
  };
  const onCancel = () => {
    cancelTransaction({ variables: { id: transaction.id } });
  };
  const onConfirm = () => {
    confirmTransaction({ variables: { id: transaction.id } });
  };
  const onSubmit = async () => {
    submitTransaction({ variables: { id: transaction.id } });
  };
  const onConfirmAndSubmit = async () => {
    confirmAndSubmitTransaction({ variables: { id: transaction.id } });
  };
  const onClickText = (text?: string) => {
    if (text) {
      setSearchText(text);
    }
  };

  const senderName = useMemo(() => {
    return (
      transaction?.historicals?.senderName ||
      transaction.sender?.name ||
      'Unkown'
    );
  }, [transaction?.historicals?.senderName, transaction.sender?.name]);

  const recipientName = useMemo(() => {
    return (
      transaction?.historicals?.recipientName ||
      transaction.recipient?.name ||
      'Unkown'
    );
  }, [transaction?.historicals?.recipientName, transaction.recipient?.name]);

  const { errors, state } = ptnTrx;
  let displayedError = '';
  let uiState = state || '';
  if (errors && errors.length > 0 && errors[0] !== null) {
    displayedError = getErrorMessage(errors[0]);
  }
  if (uiState === 'ERRORED' && displayedError.length === 0) {
    displayedError = 'An error occurred.';
  }
  if (error && displayedError.length === 0) {
    displayedError = error.message;
  }
  const loading =
    confirmResult.loading ||
    submitResult.loading ||
    confirmAndSubmitResult.loading ||
    cancelResult.loading ||
    transaction.status === 'SUBMITTED' ||
    refreshing ||
    (uiState.length > 0 && uiState !== 'ERRORED');

  return (
    <>
      <Accordion key={transaction.id}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Grid
            container
            sx={{ flexGrow: 1 }}
            alignItems="flex-start"
            direction="row"
            spacing={2}
          >
            <Grid item>
              <StatusIcon status={transaction.status} showProgress={loading} />
            </Grid>
            <Grid item xs>
              <Grid container>
                <Grid item xs={6}>
                  <Typography fontSize={15} id="nameSummary">
                    {highlightRecipient ? recipientName : senderName}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 12, color: 'text.secondary' }}
                    id="uid"
                  >
                    {transaction.id}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography fontSize={15} align="right" id="displayAmount">
                    {displayMoney(
                      displayedMoney.amount,
                      displayedMoney.currency
                    )}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 12, color: 'text.secondary' }}
                    align="right"
                    id="recipientPhone"
                  >
                    {`${canProcessMomo ? '' : '*'}${
                      transaction.recipient?.phone
                    }`}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </AccordionSummary>
        <Divider />
        <AccordionDetails>
          <Box sx={{ flexGrow: 1 }}>
            <Grid container direction="row" spacing={1}>
              <Grid
                container
                rowSpacing={1}
                columnSpacing={{ xs: 1, sm: 2, md: 3 }}
              >
                <Grid item xs={6}>
                  <SearchClickItem
                    title={!isBillPay ? 'Transfer from' : 'Payor'}
                    value={senderName}
                    secondaryValue={transaction?.sender?.phone}
                    onClickText={onClickText}
                  />
                </Grid>
                <Grid item xs={6}>
                  <SearchClickItem
                    title={!isBillPay ? 'Transfer To' : 'On behalf of'}
                    value={recipientName}
                    secondaryValue={transaction?.recipient?.phone}
                    onClickText={onClickText}
                  />
                </Grid>
                {isBillPay && (
                  <Grid item xs={6}>
                    <SearchClickItem
                      title="Service"
                      value={transaction?.bill?.serviceName || 'none'}
                    />
                  </Grid>
                )}
                {isBillPay && (
                  <Grid item xs={6}>
                    <SearchClickItem
                      title="Provider"
                      value={transaction?.bill?.merchant || 'none'}
                    />
                  </Grid>
                )}
                {isBillPay && (
                  <Grid item xs={6}>
                    <SearchClickItem
                      title="Bill date"
                      value={transaction?.bill?.date || 'none'}
                    />
                  </Grid>
                )}
                {isBillPay && (
                  <Grid item xs={6}>
                    <SearchClickItem
                      title="Bill due date"
                      value={transaction?.bill?.dueDate || 'none'}
                    />
                  </Grid>
                )}
                <Grid item xs={6}>
                  <SearchClickItem
                    title="Amount to send"
                    value={displayMoney(
                      transaction.sent.amount,
                      transaction.sent.currency
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <SearchClickItem
                    title={!isBillPay ? 'Amount to receive' : 'Bill amount'}
                    value={displayMoney(
                      transaction.received.amount,
                      transaction.received.currency
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <SearchClickItem
                    title="Rate"
                    value={`${sendExchange} = ${receiveExchange}`}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Box onClick={onCopyPtn} id="ptn-box">
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      Status
                    </Typography>
                    <Typography variant="body2" id="status">
                      {transaction.status}
                    </Typography>
                    {ptnTrx.ptn && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'bold',
                          fontSize: 11,
                          color: 'success.main',
                        }}
                      >
                        PTN Available
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  {!isBillPay ? (
                    <PhoneTypeChip phone={transaction?.recipient?.phone} />
                  ) : (
                    <RequestChip chipLabel="BillPay" />
                  )}
                </Grid>
                {transaction?.requestCount && transaction?.requestCount > 1 && (
                  <Grid item xs={6}>
                    <SearchClickItem
                      title="Request Count"
                      value={`${transaction?.requestCount}`}
                    />
                  </Grid>
                )}
              </Grid>
              {displayedError && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 1, ml: 1, padding: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: 'error.main' }}
                      id="error-msg"
                    >
                      {displayedError}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {!canProcessMomo && (
                <Grid item xs={12}>
                  <Box sx={{ ml: 1 }}>
                    <Typography
                      variant="caption"
                      align="right"
                      sx={{ fontWeight: 'normal' }}
                    >
                      * This is not an MTN or Orange number, so you cannot
                      submit through the app. Once the money has been
                      transferred, please edit the transaction and set the
                      status to Completed.
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </AccordionDetails>
        <Divider />
        <AccordionActions>
          <Box>
            <Button size="small" onClick={onDuplicate} disabled={loading}>
              {!isBillPay ? 'Duplicate' : 'New Bill'}
            </Button>
            {canCancelRequest(transaction.status) && (
              <Button size="small" onClick={onCancel} disabled={loading}>
                Cancel
              </Button>
            )}
            {transaction.status === 'SUCCESS' && (
              <Button size="small" onClick={onViewReceipt} disabled={loading}>
                Receipt
              </Button>
            )}
            <Button size="small" onClick={onEdit} disabled={loading}>
              {`${isRequestEditable(transaction.status) ? 'Edit' : 'View'}`}
            </Button>
            {transaction.status === 'REQUESTED' && (
              <Button size="small" onClick={onConfirm} disabled={loading}>
                Confirm
              </Button>
            )}
            {transaction.status === 'REQUESTED' && (
              <Button
                size="small"
                onClick={onConfirmAndSubmit}
                disabled={loading}
              >
                Confirm and Submit
              </Button>
            )}
            {transaction.status === 'CONFIRMED' && canProcessMomo && (
              <Button size="small" onClick={onSubmit} disabled={loading}>
                Submit Transfer
              </Button>
            )}
          </Box>
        </AccordionActions>
      </Accordion>
      <Snackbar
        open={showSnack}
        autoHideDuration={5000}
        message="PTN has been copied."
        onClose={() => setShowSnack(false)}
      />
    </>
  );
};

export default RequestItem;
